import { ManagedUserRoles } from "@redotech/account-common/roles";
import type { GetUser } from "@redotech/account-common/user";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import type { LocationId } from "@redotech/redo-inventory-id";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { toast } from "@redotech/redo-web/alert";
import type {
  OpenBinSelectionModalParams,
  OpenInventoryAdjustmentWarningModalParams,
} from "./bin-selection-modal";
import type { OpenInsufficientInventoryModalParams } from "./insufficient-inventory-modal";
import {
  PinVerificationConfig,
  PinVerificationType,
} from "./pin-verification-context";
import { getWmsInventoryResolution } from "./wms-inventory-resolution";

type LabelPurchaseGateClient = Pick<
  RedoOutboundLabelsRpcClient,
  "checkPrintLabelInventory" | "getAvailableBinsForFulfillmentGroups"
>;

type LabelPurchaseGateSettings = Pick<
  OutboundLabelSettings,
  "blockPrintingBeyondInventory" | "pinChecks" | "wms"
>;

interface PurchaseLabelsWithInventoryChecksParams {
  client: LabelPurchaseGateClient;
  deviceLocationId: LocationId | undefined;
  executeWithPinCheck?: (
    action: () => void,
    config: PinVerificationConfig,
    modalOptions?: { title?: string; description?: string },
  ) => void;
  fulfillmentGroupIds: readonly string[];
  onPurchase: (fulfillmentGroupIds: readonly string[]) => Promise<void>;
  onPurchaseSettled: () => void;
  openBinSelectionModal?: (params: OpenBinSelectionModalParams) => void;
  openInsufficientInventoryModal: (
    params: OpenInsufficientInventoryModalParams,
  ) => void;
  openInventoryAdjustmentWarningModal?: (
    params: OpenInventoryAdjustmentWarningModalParams,
  ) => void;
  outboundLabelSettings: LabelPurchaseGateSettings | undefined;
  user: Pick<GetUser, "managedUserRoles">;
  shippingNewSettings?: boolean;
}

export async function purchaseLabelsWithInventoryChecks(
  {
    client,
    deviceLocationId,
    executeWithPinCheck,
    fulfillmentGroupIds,
    onPurchase,
    onPurchaseSettled,
    openBinSelectionModal,
    openInsufficientInventoryModal,
    openInventoryAdjustmentWarningModal,
    outboundLabelSettings,
    shippingNewSettings = false,
    user,
  }: PurchaseLabelsWithInventoryChecksParams,
  signal?: AbortSignal,
): Promise<void> {
  const purchaseLabels = async (ids: readonly string[]) => {
    try {
      await onPurchase(ids);
    } catch (error) {
      toast("Error purchasing labels", { variant: "error" });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      console.error("Error purchasing labels", (error as Error).message);
    } finally {
      onPurchaseSettled();
    }
  };

  const purchaseAfterWmsCheck = async (ids: readonly string[]) => {
    const wmsSourceOfTruthEnabled = isWmsSotEnabledForLocation(
      outboundLabelSettings?.wms,
      deviceLocationId,
    );

    if (
      wmsSourceOfTruthEnabled &&
      (openBinSelectionModal || openInventoryAdjustmentWarningModal)
    ) {
      try {
        const { lineItems: availableBins, manualPickFulfillmentGroupIds } =
          await client.getAvailableBinsForFulfillmentGroups(
            { fulfillmentGroupIds: [...ids] },
            { signal },
          );
        const wmsInventoryResolution = getWmsInventoryResolution(availableBins);
        const isAdmin = user.managedUserRoles?.includes(ManagedUserRoles.ADMIN);
        const pinRequired =
          !isAdmin &&
          (!shippingNewSettings ||
            !!outboundLabelSettings?.pinChecks?.enabled) &&
          !!outboundLabelSettings?.pinChecks?.bypassWmsSotCheck?.enabled &&
          !!outboundLabelSettings.pinChecks.pin;
        const bypassLabel = pinRequired ? "Bypass with PIN" : "Bypass";
        const bypassAction = () => {
          const action = () => {
            void purchaseLabels(ids);
          };
          if (executeWithPinCheck) {
            executeWithPinCheck(
              action,
              { type: PinVerificationType.BypassWmsSotCheck },
              {
                title: "Bypass Bin Selection",
                description:
                  "Enter your PIN to bypass the bin selection check and proceed with label purchase.",
              },
            );
          } else {
            action();
          }
        };

        if (
          wmsInventoryResolution === "bin-selection" &&
          openBinSelectionModal
        ) {
          openBinSelectionModal({
            lineItems: availableBins,
            fulfillmentGroupIds: manualPickFulfillmentGroupIds,
            onConfirm: () => {
              void purchaseLabels(ids);
            },
            onBypass: bypassAction,
            bypassLabel,
          });
          return;
        }

        if (
          wmsInventoryResolution === "inventory-adjustment-warning" &&
          openInventoryAdjustmentWarningModal
        ) {
          openInventoryAdjustmentWarningModal({
            lineItems: availableBins,
            onConfirm: () => {
              void purchaseLabels(ids);
            },
            onBypass: bypassAction,
            bypassLabel,
          });
          return;
        }
      } catch (error) {
        toast("Error checking bin availability", { variant: "error" });
        console.error(
          "Error checking bin availability",
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (error as Error).message,
        );
        return;
      }
    }

    await purchaseLabels(ids);
  };

  if (outboundLabelSettings?.blockPrintingBeyondInventory) {
    try {
      const inventoryCheck = await client.checkPrintLabelInventory(
        { fulfillmentGroupIds: [...fulfillmentGroupIds] },
        { signal },
      );
      if (inventoryCheck.blockedGroups.length > 0) {
        openInsufficientInventoryModal({
          printableCount: inventoryCheck.printableGroupIds.length,
          blockedGroups: inventoryCheck.blockedGroups,
          onConfirm: () => {
            void purchaseAfterWmsCheck(inventoryCheck.printableGroupIds);
          },
          onPrintAll: () => {
            void purchaseAfterWmsCheck(fulfillmentGroupIds);
          },
        });
        return;
      }
    } catch (error) {
      console.error(
        "Error checking inventory availability",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        (error as Error).message,
      );
    }
  }

  await purchaseAfterWmsCheck(fulfillmentGroupIds);
}
